import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'antd';
import type { CheckboxChangeEvent } from 'antd/lib/checkbox/Checkbox';
import Button from 'components/common/button';
import Checkbox from 'components/common/input-components/checkbox';
import { SelectOption } from 'components/common/input-components/select';
import CredentialModal from 'components/organizations/credentials/credential-modal';
import { useModal } from 'hooks/modal.hooks';
import filter from 'lodash/filter';
import { FormattedMessage } from 'react-intl';
import type { CredentialProviderType, CredentialUsageType, Credential } from 'types/api.types';
import ControlledSelect from 'components/common/form-controlled/controlled-select';
import { observer } from 'mobx-react';
import { useGetAllProjectCredentials } from 'stores/rq/credentials';
import { getCredentialTypesByUsageTypeAndCredentialProvider } from 'utils/credential.utils';
import { useHasPermission } from 'hooks/use-has-permission';
import type { ProjectCredentialsForm } from 'components/projects/settings/credentials/project-settings-credentials-form.hook';
import { useCurrentProjectId } from 'hooks/path-params-extraction.hooks';
import { ProjectSettingsCredentialsSelectorOption } from 'components/projects/settings/credentials/project-settings-credentials-selector-option';
import styled from '@emotion/styled';

export interface ProjectSettingsCostsCredentialsSelectorProps {
  usageType: CredentialUsageType;
  form: ProjectCredentialsForm;
  credentialProviderControlName?: string;
  credentialProvider: CredentialProviderType;
  disabled?: boolean;
}

export const ProjectSettingsCredentialsSelector: React.FunctionComponent<ProjectSettingsCostsCredentialsSelectorProps> =
  observer(function ProjectSettingsCredentialsSelector({
    form,
    credentialProvider,
    credentialProviderControlName = credentialProvider,
    disabled,
    usageType
  }: ProjectSettingsCostsCredentialsSelectorProps) {
    const projectId = useCurrentProjectId();
    const { data: allProjectCredentials } = useGetAllProjectCredentials(projectId!);
    const { isAuthorized: hasPermission } = useHasPermission('MANAGE_CREDENTIALS');
    const cloudRelatedCredentialTypes = useMemo(
      () => getCredentialTypesByUsageTypeAndCredentialProvider(usageType, credentialProvider),
      [usageType, credentialProvider]
    );
    const credentialsForCloud = useMemo(
      () => filter(allProjectCredentials, cred => cloudRelatedCredentialTypes.includes(cred.type)),
      [allProjectCredentials, cloudRelatedCredentialTypes]
    );

    const credentialId = form.watch(credentialProviderControlName);

    const [checked, setChecked] = useState(!!credentialId);
    const [selectedEditCredential, setSelectedEditCredential] = useState<Credential | null>(null);
    const { showModal: showCreateModal, hideModal: hideCreateModal, isModalOpen: isCreateModalOpen } = useModal();
    const { showModal: showEditModal, hideModal: hideEditModal, isModalOpen: isEditModalOpen } = useModal();

    useEffect(() => {
      setChecked(!!credentialId);
    }, [credentialId]);

    const onCheckboxChanged = (evt: CheckboxChangeEvent) => {
      const checked = evt.target.checked;
      if (!checked) form.setValue(credentialProviderControlName as any, '');
      setChecked(checked);
    };

    const e2ePrefix = `project-settings-${usageType.toLowerCase()}-credentials-${credentialProvider}`;

    return (
      <Row align="middle">
        <Col span={4}>
          <Checkbox
            checked={checked}
            disabled={disabled}
            onChange={onCheckboxChanged}
            data-e2e={`${e2ePrefix}-checkbox`}>
            <FormattedMessage id={`projects.settings.menu.credentials.provider.${credentialProvider}`} />
          </Checkbox>
        </Col>
        <Col span={8}>
          <StyledSelected
            data-e2e={`${e2ePrefix}-select`}
            name={credentialProviderControlName}
            popupClassName="project-settings-credentials-selector-popup"
            form={form}
            disabled={disabled || !checked}
            customActions={
              hasPermission && (
                <Button onClick={showCreateModal} type="primary" addPlus id="add-new-credential">
                  <FormattedMessage id="settings.credentials.add.new.button.label" />
                </Button>
              )
            }>
            {credentialsForCloud.map(credential => (
              <SelectOption key={credential.id} value={credential.id} data-e2e={`${e2ePrefix}-select-option`}>
                <ProjectSettingsCredentialsSelectorOption
                  showFullName={cloudRelatedCredentialTypes.length > 1}
                  credential={credential}
                  onEditClick={() => {
                    setSelectedEditCredential(credential);
                    showEditModal();
                  }}
                />
              </SelectOption>
            ))}
          </StyledSelected>
        </Col>
        <CredentialModal
          credentialProvider={credentialProvider}
          usageType={usageType}
          open={isCreateModalOpen}
          hideModal={hideCreateModal}
        />
        {selectedEditCredential && (
          <CredentialModal
            credentialProvider={credentialProvider}
            usageType={usageType}
            open={isEditModalOpen}
            afterClose={() => {
              setSelectedEditCredential(null);
            }}
            hideModal={hideEditModal}
            credential={selectedEditCredential}
          />
        )}
      </Row>
    );
  });

const StyledSelected = styled(ControlledSelect)`
  svg {
    display: none;
  }

  .project-settings-credentials-selector-popup {
    svg {
      display: initial;
    }
  }
`;
